import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import type { ChinaTransport } from "@-/dev.improve.haniverse.data.china-transport";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useMutation, useQuery } from "@-/com.npmjs.tanstack__react-query";
import {
  tableNameMatcher,
  useTableChangeVersion,
} from "@-/dev.improve.haniverse.lib.database.change";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { useEffect, useMemo, useState } from "react";
import { deriveTravelState, type TravelState } from "#derive-travel-state";
import {
  cancelTravelTrip,
  chooseStartingPlace,
  purchaseTravelTrip,
  rideDragonTo,
  teleportTo,
} from "#travel-writes";

function useGetTravelTripsQueryKey() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const withTableChangeVersion = useTableChangeVersion(tableNameMatcher(["travel_trip"]));
  return () => withTableChangeVersion(["travelTrips", { actorDb }] as const);
}

/** Every live trip, oldest first. */
export const [useTravelTripsQueryOptions] = createUseQueryOptions(
  useGetTravelTripsQueryKey,
  async ({ queryKey: [_, { actorDb }] }) =>
    actorDb(import.meta.location).query.travelTrip.findMany({ orderBy: { departedAt: "asc" } }),
);

const NO_TRIPS: readonly never[] = [];

/**
 * The learner's current travel state. While in transit the clock ticks every second, so progress
 * advances and arrival happens on its own; otherwise nothing re-renders on a timer.
 */
export function useTravelState(): { isLoading: boolean; state: TravelState } {
  const { data: trips = NO_TRIPS, isLoading } = useQuery(useTravelTripsQueryOptions());
  const [now, setNow] = useState(() => new Date());
  const state = useMemo(() => deriveTravelState(trips, now), [trips, now]);
  const inTransit = state.kind === "in-transit";
  useEffect(() => {
    setNow(new Date());
    if (!inTransit) {
      return;
    }
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, [inTransit, trips]);
  return { isLoading, state };
}

/** Mutations for every travel action; each reads `now` when it runs. */
export function useTravelActions(transport: ChinaTransport) {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const db = () => actorDb(import.meta.location);
  const chooseStartMutation = useMutation({
    mutationFn: ({ adcode, unlocked }: { adcode: number; unlocked: boolean }) =>
      chooseStartingPlace({ actorDb: db(), adcode, now: new Date(), unlocked }),
    mutationKey: ["travel", "chooseStart"],
  });
  const purchaseMutation = useMutation({
    mutationFn: (input: {
      destinationAdcode: number;
      destinationUnlocked: boolean;
      optionId: string;
      redeemHanMiles: boolean;
    }) => purchaseTravelTrip({ actorDb: db(), now: new Date(), transport, ...input }),
    mutationKey: ["travel", "purchase"],
  });
  const teleportMutation = useMutation({
    mutationFn: ({ destinationAdcode }: { destinationAdcode: number }) =>
      teleportTo({ actorDb: db(), destinationAdcode, now: new Date() }),
    mutationKey: ["travel", "teleport"],
  });
  const rideDragonMutation = useMutation({
    mutationFn: (input: { destinationAdcode: number; destinationUnlocked: boolean }) =>
      rideDragonTo({ actorDb: db(), now: new Date(), ...input }),
    mutationKey: ["travel", "rideDragon"],
  });
  const cancelMutation = useMutation({
    mutationFn: () => cancelTravelTrip({ actorDb: db(), now: new Date() }),
    mutationKey: ["travel", "cancel"],
  });
  return {
    cancelMutation,
    chooseStartMutation,
    purchaseMutation,
    rideDragonMutation,
    teleportMutation,
  };
}
